


































































import { defineComponent } from '@vue/composition-api';
import { mapActions } from 'vuex';
import DirectorySpotsTable from '@/features/app-heating-system/views/tree-node/directory-spots/components/DirectorySpotsTable.vue';
import { APOLLO_CLIENT } from '@/features/core/container/model';
import siblingTreeNodesQuery from '@/features/app-heating-system/views/tree-node/directory-spots/graphqlQueries/siblingTreeNodes.gql';
import spotsQuery from '@/features/app-heating-system/views/tree-node/directory-spots/graphqlQueries/heatingSystemSpotsQuery.gql';
import countQuery from '@/features/app-heating-system/views/tree-node/directory-spots/graphqlQueries/heatingSystemSpotsCountQuery.gql';
import treeNodeType from '@/features/app-heating-system/views/tree-node/directory-spots/graphqlQueries/getTreeNodeType.gql';
import heatingSystemsListQuery from '@/hsc-api/queries/HeatingSytems.gql';
import { HeatingSystemSpotsQuery_spots_items_Directory_roles_roleAggregations } from '@/features/app-heating-system/views/tree-node/directory-spots/graphqlQueries/__generated__/HeatingSystemSpotsQuery';
import { Option } from '@/features/ui/inputs/model';
import { startCase } from 'lodash';
import DeviceRoleMapMixin from '@/features/core/components/mixins/device-role-map';
import heatingSystemComponentsQuery from '@/hsc-api/queries/HeatingSystemMeasurementGroupsByWhere.gql';
import { PaginationQueryStringMixin } from '@/features/core/components/mixins/pagination-query-string';
import { SpotTableSpot } from '@/features/domain-ui/spot-table/model';
import { HeatingSystemMeasurementGroupsByWhere_heatingSystemMeasurementGroups_items } from '@/hsc-api/queries/__generated__/HeatingSystemMeasurementGroupsByWhere';
import { SpotRole, TreeNodeType } from '@/types/iot-portal';

interface DirectorySpotsPanelProps {
  treeNodeId: string | undefined;
  userId: string | undefined;
}

type RoleSelectOption = {
  label: string;
  value: string;
  count: number;
};

export const ROLE_GROUPS: Partial<Record<SpotRole, SpotRole[]>> = {
  [SpotRole.GAS_METER]: [
    SpotRole.GAS_METER,
    SpotRole.INDIVIDUAL_METER_GAS_METER,
    SpotRole.SECTION_METER_GAS_METER,
    SpotRole.GENERAL_METER_GAS_METER,
  ],

  [SpotRole.HEAT_METER_COUNTER]: [
    SpotRole.HEAT_METER_COUNTER,
    SpotRole.INDIVIDUAL_METER_HEAT_METER_COUNTER,
    SpotRole.SECTION_METER_HEAT_METER_COUNTER,
    SpotRole.GENERAL_METER_HEAT_METER_COUNTER,
  ],
};

const FLOOR_REQUIRED_ROLES: SpotRole[] = [
  SpotRole.GAS_METER,
  SpotRole.HEAT_METER_COUNTER,
  SpotRole.INDIVIDUAL_METER_GAS_METER,
  SpotRole.INDIVIDUAL_METER_HEAT_METER_COUNTER,
];

const FLOOR_NOT_REQUIRED_ROLES: SpotRole[] = [
  SpotRole.SECTION_METER_HEAT_METER_COUNTER,
  SpotRole.GENERAL_METER_HEAT_METER_COUNTER,
  SpotRole.SECTION_METER_GAS_METER,
  SpotRole.GENERAL_METER_GAS_METER,
];

export default defineComponent<DirectorySpotsPanelProps, Record<string, unknown>, any, any>({
  components: { DirectorySpotsTable },
  mixins: [DeviceRoleMapMixin, PaginationQueryStringMixin],
  props: {
    treeNodeId: {
      type: String,
      required: true,
    },
    userId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      treeNodeSiblings: [],
      spots: [],
      roleAggregations: [],
      heatingSystemsList: [],
      heatingSystemMeasurementGroups: [],
      treeNodeType: null,
      // Filters
      mappedFilterOptions: [
        { label: 'All', value: 'All' },
        { label: 'Mapped', value: 'Mapped' },
        { label: 'Unmapped', value: 'Unmapped' },
      ],
      mappedFilter: { label: 'All', value: 'All' },
      role: undefined,
      // Loading variables
      treeNodesLoading: true,
      spotsLoading: true,
      heatingSystemListLoading: true,
      heatingSystemGroupsLoading: true,
    };
  },
  computed: {
    filteredRoles(): HeatingSystemSpotsQuery_spots_items_Directory_roles_roleAggregations[] {
      return this.roleAggregations.filter(
        (role: HeatingSystemSpotsQuery_spots_items_Directory_roles_roleAggregations) => {
          return role.role === 'GAS_METER' || role.role === 'HEAT_METER_COUNTER';
        },
      );
    },
    roleOptions(): Option[] {
      const aggregations = this.filteredRoles;
      if (
        this.role !== undefined &&
        aggregations.find(
          ({ role }: HeatingSystemSpotsQuery_spots_items_Directory_roles_roleAggregations) => role === this.role,
        ) === undefined
      ) {
        aggregations.push({
          __typename: 'SpotRoleAggregation',
          role: this.role,
          count: 0,
        });
      }

      return [
        ...aggregations
          .map(({ role }: HeatingSystemSpotsQuery_spots_items_Directory_roles_roleAggregations) => {
            const label = this.deviceRoleMap[role]?.label ?? startCase(role);
            // Count the spots without heating systems
            let count = 0;

            if (role === SpotRole.GAS_METER) {
              count = this.meterCounts.gas;
            } else if (role === SpotRole.HEAT_METER_COUNTER) {
              count = this.meterCounts.heat;
            }

            return { value: role, label: `${label} (${count})`, count };
          })
          .sort((a: RoleSelectOption, b: RoleSelectOption) => a.label.localeCompare(b.label)),
      ];
    },
    dataLoading(): boolean {
      return (
        this.treeNodesLoading || this.spotsLoading || this.heatingSystemListLoading || this.heatingSystemGroupsLoading
      );
    },
    filteredSpots() {
      let filteredSpots = this.spots;
      if (this.role) {
        filteredSpots = filteredSpots.filter((spot: SpotTableSpot) => {
          return this.matchesRole(spot.role, this.role);
        });
      }
      filteredSpots = filteredSpots.filter((spot: SpotTableSpot) => {
        const floor = this.getFloorLevel(spot);
        if (FLOOR_NOT_REQUIRED_ROLES.includes(spot.role)) {
          return true;
        } else if (FLOOR_REQUIRED_ROLES.includes(spot.role)) {
          return floor !== undefined && floor === -1;
        }

        return false;
      });
      if (this.mappedFilter) {
        if (this.mappedFilter === 'Mapped') {
          filteredSpots = filteredSpots.filter((spot: SpotTableSpot) => {
            const component = this.getSpotComponent(spot.id);
            return !!component;
          });
        }
        if (this.mappedFilter === 'Unmapped') {
          filteredSpots = filteredSpots.filter((spot: SpotTableSpot) => {
            const component = this.getSpotComponent(spot.id);
            return !component;
          });
        }
      }
      return filteredSpots;
    },
    meterCounts(): { gas: number; heat: number } {
      let gas = 0;
      let heat = 0;

      this.spots.forEach((spot: SpotTableSpot) => {
        const floor = this.getFloorLevel(spot);

        const isVisible =
          FLOOR_NOT_REQUIRED_ROLES.includes(spot.role) || (FLOOR_REQUIRED_ROLES.includes(spot.role) && floor === -1);

        if (!isVisible) return;

        if (ROLE_GROUPS[SpotRole.GAS_METER]?.includes(spot.role)) {
          gas++;
        } else if (ROLE_GROUPS[SpotRole.HEAT_METER_COUNTER]?.includes(spot.role)) {
          heat++;
        }
      });

      return { gas, heat };
    },
  },
  async created() {
    await this.loadData();
  },
  methods: {
    ...mapActions({
      addToastMessages: 'ADD_TOAST_MESSAGES',
    }),
    async loadData() {
      await this.loadTreeNodeSiblings();
      await this.loadTreeNodeType();
      await this.loadSpots();
      await this.loadHeatingSystemsList();
      await this.loadHeatingSystemGroups();
    },
    async loadTreeNodeSiblings(): Promise<void> {
      const { data } = await this.$apollo.query({
        query: siblingTreeNodesQuery,
        fetchPolicy: 'network-only',
        variables: { id: this.treeNodeId },
      });
      if (data?.treeNodeSiblings?.items) {
        this.treeNodeSiblings = data.treeNodeSiblings.items;
      }
      this.treeNodesLoading = false;
    },
    async loadSpots(): Promise<void> {
      const { dataCount } = await this.$apollo.query({
        query: countQuery,
        fetchPolicy: 'network-only',
        variables: {
          treeNodeId: this.treeNodeId,
          userId: this.userId,
          skip: this.skip,
          take: this.take,
        },
      });
      const count = dataCount?.spots?.items[0]?.spots?.count ?? 0;
      const { data } = await this.$apollo.query({
        query: spotsQuery,
        fetchPolicy: 'network-only',
        variables: {
          treeNodeId: this.treeNodeId,
          userId: this.userId,
          skip: this.skip,
          take: count,
        },
      });
      if (data?.spots?.items[0]?.spots?.items) {
        this.spots = data.spots?.items[0]?.spots?.items;
      }
      if (data?.spots?.items[0]?.roles?.roleAggregations) {
        this.roleAggregations = data.spots?.items[0]?.roles?.roleAggregations;
      }
      this.spotsLoading = false;
    },
    async loadTreeNodeType(): Promise<void> {
      const { data } = await this.$apollo.query({
        query: treeNodeType,
        fetchPolicy: 'network-only',
        variables: {
          id: this.treeNodeId,
        },
      });
      if (data?.treeNodes?.first) {
        this.treeNodeType = data?.treeNodes?.first.__typename;
      }
    },
    async loadHeatingSystemsList(): Promise<void> {
      const { data } = await this.$apollo.query({
        client: APOLLO_CLIENT.HEATING_SYSTEM_COLLECTOR_CLIENT,
        query: heatingSystemsListQuery,
        fetchPolicy: 'network-only',
        variables: {
          customerIdOrSiteId: this.appCustomerLastCustomerId,
          take: 5000,
        },
      });
      if (data?.heatingSystemsList?.items) {
        this.heatingSystemsList = data.heatingSystemsList.items;
      }
      this.heatingSystemListLoading = false;
    },
    async loadHeatingSystemGroups(): Promise<void> {
      const spotIds = this.spots.map((spot: SpotTableSpot) => {
        return spot.id;
      });
      const { data } = await this.$apollo.query({
        client: APOLLO_CLIENT.HEATING_SYSTEM_COLLECTOR_CLIENT,
        query: heatingSystemComponentsQuery,
        variables: { spotIds },
        fetchPolicy: 'network-only',
      });

      if (!data) {
        throw new Error('Die Exportvorlage konnte nicht gespeichert werden.');
      }

      if (data?.heatingSystemMeasurementGroups?.items) {
        this.heatingSystemMeasurementGroups = data.heatingSystemMeasurementGroups.items;
      }
      this.heatingSystemGroupsLoading = false;
    },
    changeDataValue(property: string, value: string | null) {
      this[property] = value === null ? undefined : value;
    },
    getSpotComponent(
      spotId: string,
    ): HeatingSystemMeasurementGroupsByWhere_heatingSystemMeasurementGroups_items | undefined {
      const component = this.heatingSystemMeasurementGroups.find(
        (group: HeatingSystemMeasurementGroupsByWhere_heatingSystemMeasurementGroups_items) => {
          return group.spotId === spotId;
        },
      );
      if (component) {
        return component;
      }
      return undefined;
    },
    // To refresh the data when a spot is assigned to a heating system
    async assignmentCallback() {
      await this.loadData();
    },
    matchesRole(spotRole: SpotRole, selectedRole: SpotRole): boolean {
      const group = ROLE_GROUPS[selectedRole];
      return group ? group.includes(spotRole) : false;
    },
    getFloorLevel(spot: SpotTableSpot): number | undefined {
      const subdivision = spot.path?.items?.find((p) => p.__typename === TreeNodeType.PropertySubdivision) as
        | { floor?: { level?: number } }
        | undefined;

      return subdivision?.floor?.level ?? undefined;
    },
  },
});
